import { useMutation, useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import api from '../api';
import type { ApiErrorResponse, ApiResponse } from '../types';
import { apiPayloadMapper } from '../utils';
import type {
  ShareableWorkspaceResponse,
  ShareableWorkspacePayload,
  WorkspaceMappedResponse,
} from './types';
import { workspaceResponseMapper } from './mapper';

export const addWorkspaceRequest = async (
  payload: ShareableWorkspacePayload,
): Promise<ShareableWorkspaceResponse> => {
  const response = await api.post<ShareableWorkspacePayload, ApiResponse>(
    '/workspaces/',
    apiPayloadMapper(payload),
  );

  return response.data;
};

export const getWorkspaceRequest = async ({ queryKey }) => {
  const [, workspaceId] = queryKey;
  const response = (await api.get(
    `/workspaces/${workspaceId}/`,
  )) as ApiResponse<WorkspaceMappedResponse>;

  return workspaceResponseMapper(response);
};

export const useAddWorkspace = () =>
  useMutation<
    ShareableWorkspaceResponse,
    ApiErrorResponse,
    ShareableWorkspacePayload
  >({
    mutationFn: addWorkspaceRequest,
  });

export const getWorkspaceByResourceRequest = async ({ queryKey }) => {
  const [, queryParams] = queryKey;
  const response = (await api.get(`/workspaces/resources/`, {
    params: queryParams,
  })) as ApiResponse<WorkspaceMappedResponse>;

  return workspaceResponseMapper(response);
};

export const useWorkspace = (workspaceId?: string) =>
  useQuery<WorkspaceMappedResponse, ApiErrorResponse>({
    queryKey: ['/workspace', workspaceId],
    queryFn: getWorkspaceRequest,
    enabled: !isNil(workspaceId),
  });

export const useWorkspaceByResource = (queryParams?: Record<string, string>) =>
  useQuery<WorkspaceMappedResponse, ApiErrorResponse>({
    queryKey: ['/workspace/resources', queryParams],
    queryFn: getWorkspaceByResourceRequest,
    enabled: !isNil(queryParams),
  });
